import { useEffect, useMemo } from "react";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import { IFRAME_ID } from "../Frames/constants";
import { createDebouncedRecalculatePositions } from "../Helpers/createRecalculatePositions";

const DELAY = 500;

const getIframeElementAndDocument = (): [
  HTMLIFrameElement | null,
  Document | null,
] => {
  const iframeElement = window.document.getElementById(IFRAME_ID);
  if (!(iframeElement instanceof HTMLIFrameElement)) {
    return [null, null];
  }
  const iFrameDocument = iframeElement.contentDocument;
  if (!iFrameDocument) {
    return [null, null];
  }
  return [iframeElement, iFrameDocument];
};

export const useFloatingDropZoneCalculator = () => {
  const setDropZonePositions = useBuilderStore(
    state => state.setDropZonePositions,
  );
  const pages = useBuilderStore(state => state.pages);

  const debouncedRecalculatePositions = useMemo(
    () => createDebouncedRecalculatePositions(setDropZonePositions),
    [setDropZonePositions],
  );

  // 1. Recalculate on any page content change
  useEffect(() => {
    debouncedRecalculatePositions();
  }, [pages, debouncedRecalculatePositions]);

  // 2. Recalculate on initial iframe load, after a delay
  useEffect(() => {
    const [iframeElement] = getIframeElementAndDocument();
    if (!iframeElement) return;

    const handleIframeLoad = () => {
      setTimeout(() => {
        debouncedRecalculatePositions();
      }, DELAY);
    };

    iframeElement.addEventListener("load", handleIframeLoad);
    return () => {
      iframeElement.removeEventListener("load", handleIframeLoad);
    };
  }, [debouncedRecalculatePositions]);

  // 3. Recalculate on iframe scroll
  useEffect(() => {
    let cleanupScrollListener: () => void = () => {};

    const timeoutId = setTimeout(() => {
      const [, iFrameDocument] = getIframeElementAndDocument();
      if (!iFrameDocument) return;

      iFrameDocument.addEventListener("scroll", debouncedRecalculatePositions);
      cleanupScrollListener = () => {
        iFrameDocument.removeEventListener(
          "scroll",
          debouncedRecalculatePositions,
        );
      };
    }, DELAY);

    return () => {
      clearTimeout(timeoutId);
      cleanupScrollListener();
    };
  }, [pages, debouncedRecalculatePositions]);

  // 4. Recalculate on canvas zoom or resize
  useEffect(() => {
    const resizeObserver = new ResizeObserver(debouncedRecalculatePositions);

    const timeoutId = setTimeout(() => {
      const canvasSizeElement = document.querySelector(
        '[data-testid="canvas-zoomable-size-inner"]',
      );
      if (!canvasSizeElement) return;
      resizeObserver.observe(canvasSizeElement);
    }, DELAY);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [debouncedRecalculatePositions]);

  // 5. Recalculate on individual block resize
  useEffect(() => {
    const resizeObserver = new ResizeObserver(debouncedRecalculatePositions);
    const timeoutId = setTimeout(() => {
      const [, iFrameDocument] = getIframeElementAndDocument();
      if (!iFrameDocument) return;
      const blockElements = Array.from(
        iFrameDocument.querySelectorAll("[data-measurable-block]"),
      );
      blockElements.forEach(element => resizeObserver.observe(element));
    }, DELAY);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [pages, debouncedRecalculatePositions]);

  // 6. Recalculate on image load events
  useEffect(() => {
    let cleanupImageListeners: () => void = () => {};
    let timeoutId: NodeJS.Timeout | null = null;

    const attachImageListeners = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        // First, run the cleanup for any listeners from a previous run.
        cleanupImageListeners();

        const [, iFrameDocument] = getIframeElementAndDocument();
        if (!iFrameDocument) return;

        const imageElements = Array.from(
          iFrameDocument.querySelectorAll("[data-measurable-block] img"),
        );
        imageElements.forEach(img =>
          img.addEventListener("load", debouncedRecalculatePositions),
        );

        cleanupImageListeners = () => {
          imageElements.forEach(img =>
            img.removeEventListener("load", debouncedRecalculatePositions),
          );
        };
      }, DELAY);
    };

    const [iframeElement] = getIframeElementAndDocument();
    if (!iframeElement) return;

    iframeElement.addEventListener("load", attachImageListeners);

    return () => {
      iframeElement.removeEventListener("load", attachImageListeners);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      cleanupImageListeners();
    };
  }, [pages, debouncedRecalculatePositions]);
};
